import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { ScoreDisplay, ScoreBar } from '../components/ScoreDisplay';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Building2,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExecutiveDashboard() {
  const [loading, setLoading] = useState(true);
  const [rankings, setRankings] = useState({ daily: [], weekly: [], monthly: [] });
  const [period, setPeriod] = useState('daily');
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState({
    totalSales: 0,
    avgScore: 0,
    locationCount: 0,
    pendingEntries: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Fetch all rankings
      const [dailyRes, weeklyRes, monthlyRes, locationsRes] = await Promise.all([
        axios.get(`${API}/rankings`, { params: { date: today, period: 'daily' } }),
        axios.get(`${API}/rankings`, { params: { date: today, period: 'weekly' } }),
        axios.get(`${API}/rankings`, { params: { date: today, period: 'monthly' } }),
        axios.get(`${API}/locations`)
      ]);

      setRankings({
        daily: dailyRes.data.rankings || [],
        weekly: weeklyRes.data.rankings || [],
        monthly: monthlyRes.data.rankings || []
      });
      
      setLocations(locationsRes.data);

      // Calculate stats from weekly data
      const weeklyData = weeklyRes.data.rankings || [];
      setStats({
        totalSales: weeklyData.reduce((sum, r) => sum + r.total_sales, 0),
        avgScore: weeklyData.length > 0 
          ? weeklyData.reduce((sum, r) => sum + r.average_score, 0) / weeklyData.length 
          : 0,
        locationCount: locationsRes.data.length,
        pendingEntries: dailyRes.data.rankings?.filter(r => r.entry_count === 0).length || 0
      });
    } catch (error) {
      console.error('Failed to fetch executive data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankBadgeClass = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white';
    if (rank === 2) return 'bg-gradient-to-r from-gray-300 to-gray-400 text-gray-800';
    if (rank === 3) return 'bg-gradient-to-r from-orange-400 to-orange-500 text-white';
    return 'bg-slate-100 text-slate-600';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  const currentRankings = rankings[period] || [];

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="executive-dashboard">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
              Executive Overview
            </h1>
            <p className="text-slate-600 mt-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button asChild className="bg-violet-600 hover:bg-violet-700">
              <Link to="/compare">
                Compare Locations
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Weekly Sales</p>
                  <p className="text-xl font-bold font-mono">{formatCurrency(stats.totalSales)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-100">
                  <TrendingUp className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Avg Score</p>
                  <p className="text-xl font-bold font-mono">{stats.avgScore.toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Locations</p>
                  <p className="text-xl font-bold font-mono">{stats.locationCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Today</p>
                  <p className="text-xl font-bold font-mono">{stats.pendingEntries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rankings */}
        <Card data-testid="rankings-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-lg">Location Rankings</CardTitle>
                <CardDescription>Performance comparison across all locations</CardDescription>
              </div>
              
              <Tabs value={period} onValueChange={setPeriod}>
                <TabsList>
                  <TabsTrigger value="daily" data-testid="tab-daily">Daily</TabsTrigger>
                  <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly</TabsTrigger>
                  <TabsTrigger value="monthly" data-testid="tab-monthly">Monthly</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {currentRankings.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No data available for this period</p>
              </div>
            ) : (
              <div className="space-y-4">
                {currentRankings.map((location, index) => (
                  <div 
                    key={location.location_id}
                    className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
                    data-testid={`ranking-row-${location.location_id}`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${getRankBadgeClass(location.rank)}`}>
                      {location.rank}
                    </div>
                    
                    {/* Location Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-900 truncate">{location.location_name}</h4>
                        <Badge variant="outline" className="text-xs">{location.region}</Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span>{location.entry_count} entries</span>
                        <span>{formatCurrency(location.total_sales)} sales</span>
                      </div>
                    </div>
                    
                    {/* Score */}
                    <div className="flex items-center gap-4">
                      <div className="hidden sm:block w-32">
                        <ScoreBar score={location.average_score} />
                      </div>
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-mono font-bold text-lg text-white ${
                        location.average_score >= 85 ? 'bg-emerald-500' :
                        location.average_score >= 70 ? 'bg-lime-500' :
                        location.average_score >= 55 ? 'bg-amber-500' : 'bg-red-500'
                      }`}>
                        {Math.round(location.average_score)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
